import { useEffect, useRef, useState } from "react";

const wrap = (index: number, length: number) => ((index % length) + length) % length;

// Keep each visible slide mounted while it travels across the carousel. The
// extra slides enter outside the visible area, including at the loop boundary.
export function useCarousel(length: number, radius: number) {
  const [cursor, setCursor] = useState(0);
  const position = useRef(0);
  const destination = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStart = useRef<number | null>(null);

  useEffect(() => {
    position.current = 0;
    destination.current = 0;
    setCursor(0);
    return () => {
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [length]);

  const advance = () => {
    if (timer.current !== null || position.current === destination.current) return;
    position.current += Math.sign(destination.current - position.current);
    setCursor(position.current);
    const duration = window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 900;
    timer.current = setTimeout(() => {
      timer.current = null;
      advance();
    }, duration);
  };

  const change = (direction: number) => {
    if (length < 2) return;
    destination.current += direction;
    advance();
  };

  const goTo = (index: number) => {
    if (length < 2) return;
    let distance = wrap(index - destination.current, length);
    if (distance > length / 2) distance -= length;
    destination.current += distance;
    advance();
  };

  const slots = length < 2
    ? (length ? [{ key: 0, index: 0, position: 0 }] : [])
    : Array.from({ length: radius * 2 + 3 }, (_, offset) => {
      const slotPosition = offset - radius - 1;
      const key = cursor + slotPosition;
      return { key, index: wrap(key, length), position: slotPosition };
    });

  return {
    active: length ? wrap(cursor, length) : 0,
    slots,
    change,
    goTo,
    swipe: {
      onTouchStart: (event: React.TouchEvent) => { touchStart.current = event.touches[0].clientX; },
      onTouchEnd: (event: React.TouchEvent) => {
        if (touchStart.current === null) return;
        const distance = touchStart.current - event.changedTouches[0].clientX;
        if (Math.abs(distance) > 45) change(Math.sign(distance));
        touchStart.current = null;
      },
      onTouchCancel: () => { touchStart.current = null; },
    },
  };
}
